// CORE //
import { JL } from 'jsnlog';
import { CoreModule } from '../core/core.module';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, UrlSerializer } from '@angular/router';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { AuthModule } from '@auth0/auth0-angular';

// SHARED //
import { GlobalErrorHandler } from '../services/global-error-handler';
import { HttpLoggerInterceptor } from '../api/http-logger.interceptor';
import { HtmlDecodePipe } from '../shared/pipes/html-decode.pipe';
import { SentenceCasePipe } from '../shared/pipes/sentence-case.pipe';
import { SplitUpperCasePipe } from '../shared/pipes/split-upper-case.pipe';
import { RiskCalulationPipe } from '../ai-assessments/pipes/risk-calulation.pipe';
import { RiskCalulationToTextPipe } from '../ai-assessments/pipes/risk-calculation-to-text.pipe';
import { CamelCaseWithSpacesPipe } from '../shared/pipes/camel-case-with-spaces.pipe';
import { LowerCaseFirstLetterPipe } from '../shared/pipes/lower-case-first-letter.pipe';
import { AssessmentStatusPipe } from '../ai-assessments/pipes/assessment-status.pipe';
import { ConvertPiiTypeToDataTypePipe } from '../shared/pipes/convert-pii-type-to-data-type.pipe';
import { CustomEntityChipBackgroundColorPipe } from '../ai-assessments/pipes/custom-entity-chip-color.pipe';
import { MineSnackbarComponent } from '../shared/mine-snackbar/mine-snackbar.component';
import { AiAssessmentLabelsPipe } from '../ai-assessments/pipes/ai-assessment-labels.pipe';
import { NormalizeKeyPipe } from '../shared/pipes/normalize-key.pipe';
import { RandomUUIDPipe } from '../shared/pipes/random-uuid.pipe';
import { EllipsisNamePipe } from '../shared/pipes/ellipsis-name.pipe';

// SERVICES //
import { CustomUrlSerializer } from '../services/custom-url-serializer.service';
import { CollaborationBootstrapService } from './services/collaboration-bootstrap.service';
import { CollaborationHttpErrorsInterceptor } from './services/collaboration-http-errors.interceptor';
import { ApiClientCollaborationFeaturesService } from './api/api-client-collaboration-features.service';
import { ApiClientCollaborationSystemsService } from './api/api-client-collaboration-systems.service';
import { CollaborationHttpHeadersInterceptor } from './services/collaboration-http-header.interceptor';
import { HtmlDecodeInterceptor } from '../api/html-decode.interceptor';
import { CollaborationNavigationService } from './services/collaboration-navigation.service';
import { CollaborationUserPropertiesService } from './services/collaboration-user-properties.service';
import { ApiClientCollaborationRisksService } from './api/api-client-collaboration-risks.service';
import { ApiClientCollaborationEmployeesService } from './api/api-client-collaboration-employees.service';
import { ApiClientCollaborationAssessmentsService } from './api/api-client-collaboration-assessments.service';
import { ApiClientCollaborationCompanySettingsService } from './api/api-client-collaboration-company-settings.service';
import { ApiClientCollaborationLabelsService } from './api/api-client-collaboration-lables.service';
import { ApiClientCollaborationCustomFieldsV2Service } from './api/api-client-collaboration-custom-fields-v2.service';

// MODULES //
import { ContentPipeModule } from '../services/content/content-pipe.module';
import { CollaborationRoutingModule } from './collaboration-routing.module';
import { environment } from 'src/environments/environment';
import { RoutesManager } from '../shared/models/routes.interfaces';

// INJECTORS //
import { API_CLIENT_RISKS_SERVICE } from '../api/injectors/api-client-risks.token';
import { API_CLIENT_LABELS_SERVICE } from '../api/injectors/api-client-labels.token';
import { API_CLIENT_EMPLOYEES_SERVICE } from '../api/injectors/api-client-employees.token';
import { API_CLIENT_COMPANY_SETTINGS_SERVICE } from '../api/injectors/api-client-company-settings.token';
import { API_CLIENT_CUSTOM_FIELDS_V2_SERVICE } from '../api/injectors/api-client-custom-fields-v2.token';
import { API_CLIENT_ASSESSMENTS_SERVICE } from '../api/injectors/api-client-ai-assessments.token';
import { API_CLIENT_SYSTEMS_SERVICE } from '../api/injectors/api-client-systems-service.token';
import { FEATURE_FLAGS_API_SERVICE } from '../feature-flag/feature-flag-injection-token';
import { USER_PROPERTIES_SERVICE } from '../analytics/user-properties-injection-token';

// COMPONENTS //
import { CollaborationComponent } from './collaboration.component';

const pipes = [
	DatePipe,
	TitleCasePipe,
	HtmlDecodePipe,
	SentenceCasePipe,
	SplitUpperCasePipe,
  RiskCalulationPipe,
  AssessmentStatusPipe,
  RiskCalulationToTextPipe,
	CamelCaseWithSpacesPipe,
  LowerCaseFirstLetterPipe,
  ConvertPiiTypeToDataTypePipe,
  CustomEntityChipBackgroundColorPipe,
  AiAssessmentLabelsPipe,
  NormalizeKeyPipe,
  RandomUUIDPipe,
  EllipsisNamePipe
];

const auth0Config = {
	domain: environment.collaboration.auth0.domain,
	clientId: environment.collaboration.auth0.clientId,
	authorizationParams: {
		redirect_uri: `${window.location.origin}/${RoutesManager.collaborationAuthProxy}`,
		audience: environment.collaboration.auth0.audience,
		scope: 'openid profile email offline_access',
	},
	useRefreshTokens: true,
	cacheLocation: 'localstorage' as const,
	useCookiesForTransactions: true,
};

@NgModule({
  declarations: [
    CollaborationComponent,
  ],
  imports: [
    CoreModule,
    CommonModule,
    BrowserModule,
    ReactiveFormsModule,
    MineSnackbarComponent,
    ContentPipeModule,
    RouterModule,
    CollaborationRoutingModule,
    AuthModule.forRoot(auth0Config),
  ],
  providers: [
    [...pipes],
    CollaborationNavigationService,
    { provide: 'JSNLOG', useValue: JL },
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => inject(CollaborationBootstrapService).preLoad()),
    { provide: HTTP_INTERCEPTORS, useClass: HttpLoggerInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CollaborationHttpErrorsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CollaborationHttpHeadersInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HtmlDecodeInterceptor, multi: true },
    { provide: FEATURE_FLAGS_API_SERVICE, useClass: ApiClientCollaborationFeaturesService },
    { provide: USER_PROPERTIES_SERVICE, useClass: CollaborationUserPropertiesService },
    { provide: API_CLIENT_SYSTEMS_SERVICE, useClass: ApiClientCollaborationSystemsService },
    { provide: API_CLIENT_COMPANY_SETTINGS_SERVICE, useClass: ApiClientCollaborationCompanySettingsService },
    { provide: API_CLIENT_CUSTOM_FIELDS_V2_SERVICE, useClass: ApiClientCollaborationCustomFieldsV2Service },
    { provide: API_CLIENT_ASSESSMENTS_SERVICE, useClass: ApiClientCollaborationAssessmentsService },
    { provide: API_CLIENT_RISKS_SERVICE, useClass: ApiClientCollaborationRisksService },
    { provide: API_CLIENT_LABELS_SERVICE, useClass: ApiClientCollaborationLabelsService },
    { provide: API_CLIENT_EMPLOYEES_SERVICE, useClass: ApiClientCollaborationEmployeesService },
		{ provide: ErrorHandler, useClass: GlobalErrorHandler, },
		{ provide: UrlSerializer, useClass: CustomUrlSerializer },
  ],
  bootstrap: [CollaborationComponent]
})
export class CollaborationAppModule {}